import { Button, Flex, Input, InputGroup, InputRightElement, Text, Tooltip } from '@chakra-ui/react';
import { ReactComponent as ArrowIcon } from 'assets/icons/arrow_right.svg';
import { EDITOR_VERSION, INVITE_BUTTON } from 'constants/mixpanel';
import { checkUsageTeamMembers } from 'libs/e1api/team';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { validateEmail } from 'utils/email';

import { UpgradeLock } from '../DownloadModal/OptionTitle';
import { InviteTeamMemberModal } from './InviteTeamMemberModal';
import { InviteTeamMemberProps } from './SharePanel.types';

type InviteTeamMembersProps = {
  disabled?: boolean;
  hideLock?: boolean;
  addLeftMargin?: boolean;
  addYMargin?: boolean;
  onUpgrade?: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  onInvite?: (email: string, done?: () => void) => void;
  inviteTeamMember: InviteTeamMemberProps;
  setInviteTeamMember: (value: InviteTeamMemberProps) => void;
};

enum InviteTeamMemberErrorType {
  ValidateEmail = 'validateEmail',
  CheckUserTeamMembers = 'checkUserTeamMembers',
  CheckUsageTeamMembers = 'checkUsageTeamMembers',
  AddTeamSeats = 'addTeamSeats',
  AddTeamMember = 'addTeamMember',
  OtherError = 'otherError',
}

export const InviteTeamMembers = ({
  disabled = false,
  hideLock = false,
  addLeftMargin = false,
  addYMargin = false,
  onUpgrade = () => {},
  onInvite = () => {},
  inviteTeamMember,
  setInviteTeamMember,
}: InviteTeamMembersProps) => {
  const [value, setValue] = useState('');
  const { t } = useTranslation('common_navbar');
  const { status: isInvalidEmail, errType } = inviteTeamMember;
  const [isLoading, setIsLoading] = useState(false);
  const [showInviteMemberModal, setShowInviteMemberModal] = useState(false);
  const showUpgradeLock = disabled && !hideLock;

  const [seats, setSeats] = useState(0);
  const [refreshRooms, setRefreshRooms] = useState(false);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const fetchedData = await checkUsageTeamMembers('');
        setSeats(fetchedData.rooms);
      } catch (err: any) {
        // Output error to console.
        console.error(err);
        // This will just show the default screen, if 0 seats, we just display the pricing per member as usual, no info misleading the user.
        setSeats(0);
      }
    };

    fetchData();
  }, [refreshRooms]);

  const handleChange = (event: FormEvent<HTMLInputElement>) => setValue(event.currentTarget.value);

  const handleSendInviteButtonClick = () => {
    setRefreshRooms(!refreshRooms);
    if (value && validateEmail(value)) {
      // Mixpanel event
      Mixpanel.track(INVITE_BUTTON.eventName, { editor_version: EDITOR_VERSION, member_email: value });

      setInviteTeamMember({ status: false, errType: '' });
      setShowInviteMemberModal(true);
    } else {
      setInviteTeamMember({ status: true, errType: InviteTeamMemberErrorType.ValidateEmail });
    }
  };

  const handleInviteModalClose = () => {
    setIsLoading(false);
    setShowInviteMemberModal(false);
  };

  const handleInviteTeamMemberClick = () => {
    setIsLoading(true);
    onInvite(value, () => {
      setIsLoading(false);
      setShowInviteMemberModal(false);
    });
  };

  const inviteTeamMembersTestId = 'invite-team-members';
  const upgradeLockTestId = 'invite-team-members-upgrade-lock';
  const sendInviteInputTestId = 'invite-team-members-send-invite-input';
  const sendInviteButtonTestId = 'invite-team-members-send-invite-button';

  return (
    <>
      <Flex flexDir='column' gap='2' data-testid={inviteTeamMembersTestId}>
        {showUpgradeLock && (
          <Flex
            flexDir='row'
            justifyContent='space-between'
            alignItems='center'
            mt={addYMargin ? '4' : '2'}
            mb={addYMargin ? '4' : '2'}
            ml={addLeftMargin ? '2' : '0'}
            color='font.500'
            data-testid={upgradeLockTestId}
          >
            <UpgradeLock
              handleClickUpgrade={onUpgrade}
              upgradeText={t('modals.download.upgrade-business')}
              postText={t('modals.share-panel.invite-team-members.post-text-collaborate')}
            />
          </Flex>
        )}

        <Text fontWeight={'semibold'} fontSize='sm' lineHeight='1.25' color={'font.500'}>
          {t('modals.share-panel.invite-team-members.title')}
        </Text>

        <Flex gap='2'>
          <InputGroup size='sm'>
            <Input
              pr='4.5rem' // Padding right to ensure text doesn't go under the button
              value={value}
              onChange={handleChange}
              placeholder='Enter an email address'
              disabled={disabled}
              type='email'
              isInvalid={isInvalidEmail}
              errorBorderColor='red.300'
              data-testid={sendInviteInputTestId}
              borderRadius={'base'}
            />
            <InputRightElement width='2.5rem'>
              <Tooltip label={t('modals.share-panel.send-invite')} placement='bottom' hasArrow>
                <Button
                  h='1.5rem'
                  size='sm'
                  bg={'transparent'}
                  onClick={handleSendInviteButtonClick}
                  disabled={disabled}
                  data-testid={sendInviteButtonTestId}
                  aria-label={t('modals.share-panel.send-invite')}
                  p={0}
                >
                  <ArrowIcon />
                </Button>
              </Tooltip>
            </InputRightElement>
          </InputGroup>
        </Flex>
        {isInvalidEmail && <InviteTeamMembersErrorMsg errType={errType} />}
      </Flex>
      <InviteTeamMemberModal
        isOpen={showInviteMemberModal}
        onClick={handleInviteTeamMemberClick}
        onClose={handleInviteModalClose}
        isLoading={isLoading}
        seats={seats}
        loadingText={t('modals.share-panel.invite-team-members.loading-text')}
      />
    </>
  );
};

const InviteTeamMembersErrorMsg = ({ errType }: { errType: string }) => {
  const { t } = useTranslation('common_navbar');

  const errMsgTypeFunc = (errType: string) => {
    switch (errType) {
      case InviteTeamMemberErrorType.ValidateEmail:
        return t('inviteTeamMembersErrorMsg.validateEmail');
      case InviteTeamMemberErrorType.CheckUserTeamMembers:
        return t('inviteTeamMembersErrorMsg.checkUserTeamMembers');
      case InviteTeamMemberErrorType.CheckUsageTeamMembers:
        return t('inviteTeamMembersErrorMsg.checkUsageTeamMembers');
      case InviteTeamMemberErrorType.AddTeamSeats:
        return t('inviteTeamMembersErrorMsg.addTeamSeats');
      case InviteTeamMemberErrorType.AddTeamMember:
        return t('inviteTeamMembersErrorMsg.addTeamMember');
      case InviteTeamMemberErrorType.OtherError:
      default:
        return t('inviteTeamMembersErrorMsg.otherError');
    }
  };

  return (
    <Text color={'red.500'} fontSize={'xs'}>
      {errMsgTypeFunc(errType)}
    </Text>
  );
};
